import React from 'react';
import { Controller, useFormContext, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { SelectCombobox } from '@/shared/ui/Combobox/SelectCombobox.tsx';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';
import { cn } from '@/shared/utils/cn.ts';

type UnitFieldValues = { [key: string]: SUPPORTED_UNITS };

export type UnitInputProps = Pick<UseControllerProps<UnitFieldValues>, 'name' | 'defaultValue'> & {
    variant?: 'lg' | 'md';
    disabled?: boolean;
    options?: SUPPORTED_UNITS[];
    onUnitChange?: (unit?: SUPPORTED_UNITS) => void;
    className?: string;
};

export const UnitInput = React.forwardRef<HTMLInputElement, UnitInputProps>(
    ({ name = 'unit', defaultValue, disabled, variant = 'lg', options = [], onUnitChange, className }, _ref) => {
        const { control } = useFormContext();
        const { t } = useTranslation();

        return (
            <Controller
                name={name}
                defaultValue={defaultValue ?? null}
                control={control}
                render={({ field: { ref, onChange, value }, fieldState }) => (
                    <SelectCombobox
                        fieldRef={ref}
                        value={value ?? undefined}
                        onValueChange={unit => {
                            onUnitChange?.(unit);
                            onChange(unit);
                        }}
                        options={options.map(unit => ({
                            value: unit,
                            label: t(SUPPORTED_UNIT_LABEL[unit]),
                        }))}
                        disabled={disabled}
                        placeholder={t('product.quote.unit')}
                        error={fieldState.error?.message}
                        size={variant}
                        dataTest="unit-select-input"
                        className={cn(className)}
                    />
                )}
            />
        );
    }
);
